/* eslint-disable react-hooks/exhaustive-deps */
import { useDispatch, useSelector } from "react-redux";
import { resourceUpdated, selectResourceById } from "../slices/resourceCollection/resourceCollectionSlice";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { emptyReasonNotStarted, sectionCodeResults } from "../dictionaries/CodeSystemLookup";
import { Button, Table } from "semantic-ui-react";
import { displayStringFromFHIRCodeableConcept } from "../datatypeComponents/CodeableConceptEntry";
import { DisplayFromFHIRReference } from "../datatypeComponents/ReferenceEntry";
import { AddTableRowModal, EditTableCellEvidenceOnlyModal } from "./EditTableCellModal";
import getIdFromReference from "../functions/getIdFromReference";
import updateChangeReport from "../slices/resourceChangeReport/updateChangeReport";
import { selectAllResourceChanges } from "../slices/resourceChangeReport/resourceChangeReportSlice";

const autoEditEmptyReason = (newSection) => {
    if (newSection) {
        if (newSection.section?.length || newSection.entry?.length ||
            (newSection.text && newSection.text.status !== "empty")) {
            if (newSection.emptyReason) {
                delete newSection.emptyReason;
            }
        } else if (!newSection.emptyReason) {
            newSection.emptyReason = emptyReasonNotStarted;
        }
    }
    return newSection;
}

const EvidenceReferenceDataEntry = memo(({ startingValue,
    outcomeName, outcomeFocus, outcomeGroup, setEntryArrayState, resourceLoaded,
    statisticType, statisticUnit, knownDataCount }) => {

    let compositionId = resourceLoaded.json.id;
    let compositionTitle = resourceLoaded.title || resourceLoaded.json.title || resourceLoaded.json.name;
    let combinedDictionary = resourceLoaded.combinedDictionary;
    let evidenceDisplayDictionary = resourceLoaded.evidenceDisplayDictionary;
    let suggestedEvidenceTitle = "Evidence for " + outcomeName + " in " + outcomeGroup.display;
    let partOfReference = {
        "reference": "Composition/" + compositionId,
        "type": "Composition",
        "display": compositionTitle
    }
    let variableDefinition = [{
        "variableRole": "population",
        "observed": outcomeGroup
    }, {
        "variableRole": "outcome",
        "observed": outcomeFocus
    }];
    let entryAddElementValues = {
        title: combinedDictionary?.[startingValue?.entry?.[0]?.reference?.split("/")[1]]?.title || suggestedEvidenceTitle,
        relatesTo: [{ "type": "part-of", "targetReference": partOfReference }],
        description: combinedDictionary?.[startingValue?.entry?.[0]?.reference?.split("/")[1]]?.description || "",
        variableDefinition: variableDefinition,
        statistic: combinedDictionary?.[startingValue?.entry?.[0]?.reference?.split("/")[1]]?.statistic || ""
    };

    let startingEntryInstance = {
        reference: "", type: "Evidence", identifier: "", display: ""
    }
    if (!startingValue) {
        startingValue = "";
    } else {
        if (startingValue.extension) { startingEntryInstance.extension = startingValue.extension; }
        if (startingValue.reference) { startingEntryInstance.reference = startingValue.reference; }
        if (startingValue.type) { startingEntryInstance.type = startingValue.type; }
        if (startingValue.identifier) { startingEntryInstance.identifier = startingValue.identifier; }
        if (startingValue.display) { startingEntryInstance.display = startingValue.display; }
    }

    const [openEditModalState, setOpenEditModalState] = useState({ "openModal": false });
    const [entryInstanceState, setEntryInstanceState] = useState(JSON.parse(JSON.stringify(startingEntryInstance || {})));

    useEffect((() => {
        if (Object.keys(entryInstanceState).length > 0) {
            let newReference = {};
            if (entryInstanceState.extension) { newReference.extension = entryInstanceState.extension; }
            if (entryInstanceState.reference) { newReference.reference = entryInstanceState.reference; }
            if (entryInstanceState.type) { newReference.type = entryInstanceState.type; }
            if (entryInstanceState.identifier) { newReference.identifier = entryInstanceState.identifier; }
            if (entryInstanceState.display) { newReference.display = entryInstanceState.display; }
            if (Object.keys(newReference).length === 0 ||
                (Object.keys(newReference).length === 1 && newReference.type === "Evidence")) {
                newReference = null;
            }
            setEntryArrayState(prevState => {
                return { ...prevState, [getIdFromReference(outcomeGroup)]: newReference }
            });
        }
    }), [entryInstanceState]);

    if (openEditModalState?.openModal) {
        return <EditTableCellEvidenceOnlyModal
            entryInstance={entryInstanceState} setEntryInstanceState={setEntryInstanceState}
            addElementValues={entryAddElementValues}
            setOpenEditModalState={setOpenEditModalState}
            resourceLoaded={resourceLoaded}
            statisticType={statisticType} statisticUnit={statisticUnit}
            knownDataCount={knownDataCount} />
    } else {
        return <div>
            {startingValue && <div style={{ marginLeft: "24px" }}>
                {(getIdFromReference(entryInstanceState) &&
                    evidenceDisplayDictionary[getIdFromReference(entryInstanceState)]) &&
                    <div><b>Summary: </b>{evidenceDisplayDictionary[getIdFromReference(entryInstanceState)]}
                    </div>
                }
                <br />
                <DisplayFromFHIRReference reference={startingValue} />
                <br />
            </div>}
            &nbsp;&nbsp;
            <span className={"unselectable"} style={{ cursor: "pointer" }}
                onClick={() => {
                    setOpenEditModalState({ openModal: true });
                }}>
                {startingValue ? <>✎ Edit</> : <>➕ Add</>}
            </span>
        </div>
    }

});

const TableRowSectionDataEntry = memo(({ startingValue, resourceLoaded, setTableRowSectionState,
    sampleSizeDefaultState }) => {

    let outcomeName = startingValue?.title || startingValue?.focus?.display;
    let combinedDictionary = resourceLoaded.combinedDictionary;
    let groupReferencesDictionary = resourceLoaded.groupReferencesDictionary;
    let groupKeys = Object.keys(groupReferencesDictionary);

    const [entryArrayState, setEntryArrayState] = useState(null);
    const [startingEntryArrayState, setStartingEntryArrayState] = useState(null);
    const [statisticDefaultsState, setStatisticDefaultsState] = useState({
        "statisticType": "", "units": ""
    });
    const [selectedStatisticDefaultsState, setSelectedStatisticDefaultsState] = useState({
        "statisticType": "", "units": ""
    });

    useEffect(() => {
        let startingEntryStateValues = {};
        let defaultStatisticType = "";
        let defaultStatisticUnits = "";
        if (startingValue?.entry?.length) {
            for (let itemIndex in startingValue.entry) {
                let evidenceReference = startingValue.entry[itemIndex];
                let evidenceId = getIdFromReference(evidenceReference);
                let item = combinedDictionary[evidenceId];
                let entryJson;
                if (item?.resourceType === "Evidence") {
                    if (item.variableDefinition?.[1]?.variableRole === "exposure") {
                        entryJson = item.variableDefinition[1].observed || item.variableDefinition[1].intended;
                    } else if (item.variableDefinition?.[0]?.variableRole === "population") {
                        entryJson = item.variableDefinition[0].observed || item.variableDefinition[0].intended;
                    }
                }
                if (entryJson) {
                    let entryId = getIdFromReference(entryJson);
                    startingEntryStateValues[entryId] = evidenceReference;
                }
                if (!defaultStatisticType && combinedDictionary &&
                    combinedDictionary[evidenceId]) {
                    let evidenceJson = combinedDictionary[evidenceId];
                    if (evidenceJson.statistic?.[0]?.statisticType) {
                        defaultStatisticType = evidenceJson.statistic[0].statisticType;
                        if (!defaultStatisticUnits && evidenceJson.statistic?.[0]?.quantity?.unit) {
                            defaultStatisticUnits = evidenceJson.statistic[0].quantity.unit;
                        }
                    }
                }
            }
        }
        setEntryArrayState(JSON.parse(JSON.stringify(startingEntryStateValues)));
        setStartingEntryArrayState(JSON.parse(JSON.stringify(startingEntryStateValues)));
        setStatisticDefaultsState({
            "statisticType": defaultStatisticType, "units": defaultStatisticUnits
        });
    }, [])

    useEffect(() => {
        if (entryArrayState && JSON.stringify(entryArrayState) !== JSON.stringify(startingEntryArrayState)) {
            let newEntryArray = [];
            for (const key in entryArrayState) {
                if (entryArrayState[key]) {
                    newEntryArray.push(entryArrayState[key]);
                }
            }
            if (newEntryArray?.length) {
                setTableRowSectionState(prevState => { return { ...prevState, "entry": newEntryArray } });
            }
        }
    }, [entryArrayState]);

    return <>
        <Table.Cell style={{ verticalAlign: "top" }}>
            {startingValue.title || startingValue.focus?.display}
            <p><span className={"unselectable"} style={{ cursor: "pointer" }}
                onClick={() => {
                    setSelectedStatisticDefaultsState({
                        "statisticType": statisticDefaultsState.statisticType || "",
                        "units": statisticDefaultsState.units || ""
                    })
                }}>
                {(statisticDefaultsState.statisticType && !selectedStatisticDefaultsState.statisticType) &&
                    <>➕ Add Default Statistic Type {displayStringFromFHIRCodeableConcept(statisticDefaultsState.statisticType)} and Units {statisticDefaultsState.units || "none"}</>}
            </span></p>
        </Table.Cell>
        {entryArrayState && groupKeys.map((groupKey, groupIndex) => {
            return <Table.Cell key={groupIndex} style={{ verticalAlign: "top" }}>
                <span>
                    <EvidenceReferenceDataEntry startingValue={entryArrayState[groupKey]}
                        outcomeName={outcomeName} outcomeFocus={startingValue.focus}
                        outcomeGroup={groupReferencesDictionary[groupKey]} setEntryArrayState={setEntryArrayState}
                        statisticType={selectedStatisticDefaultsState.statisticType}
                        statisticUnit={selectedStatisticDefaultsState.units}
                        knownDataCount={sampleSizeDefaultState[groupKey]}
                        resourceLoaded={resourceLoaded} />
                </span>
            </Table.Cell>
        })}
    </>
});

const TableRowEdit = memo(({ startingValue, resourceLoaded, sectionPathString,
    sampleSizeDefaultState, foi }) => {

    const parentResourceDetail = useSelector(state => selectResourceById(state, foi));
    const resourceChanges = useSelector(state => selectAllResourceChanges(state));

    const calculateStartingSection = useCallback((startingValue) => {
        let startingSection = {
            extension: [],
            title: "", code: sectionCodeResults, focus: "", text: "", orderedBy: "", entry: [], emptyReason: "", section: []
        }
        if (startingValue) {
            if (startingValue.extension) { startingSection.extension = startingValue.extension; }
            else { delete startingSection.extension; }
            if (startingValue.title) { startingSection.title = startingValue.title; }
            else { delete startingSection.title; }
            if (startingValue.code) { startingSection.code = startingValue.code; }
            if (startingValue.author) { startingSection.author = startingValue.author; }
            else { delete startingSection.author; }
            if (startingValue.focus) { startingSection.focus = startingValue.focus; }
            else { delete startingSection.focus; }
            if (startingValue.text) { startingSection.text = startingValue.text; }
            else { delete startingSection.text; }
            if (startingValue.orderedBy) { startingSection.orderedBy = startingValue.orderedBy; }
            else { delete startingSection.orderedBy; }
            if (startingValue.entry) { startingSection.entry = startingValue.entry; }
            if (startingValue.emptyReason) { startingSection.emptyReason = startingValue.emptyReason; }
            else { delete startingSection.emptyReason; }
            if (startingValue.section) { startingSection.section = startingValue.section; }
            else { delete startingSection.section; }
        }
        return startingSection;
    }, []);
    const startingSection = useMemo(() => calculateStartingSection(startingValue), [startingValue, calculateStartingSection]);

    const [tableRowSectionState, setTableRowSectionState] = useState(JSON.parse(JSON.stringify(startingSection || {})));
    const dispatch = useDispatch();

    useEffect((() => {
        if (Object.keys(tableRowSectionState).length > 0 &&
            (JSON.stringify(startingSection) !== JSON.stringify(tableRowSectionState))) {
            let newSection = {};
            if (tableRowSectionState.extension) { newSection.extension = tableRowSectionState.extension; }
            if (tableRowSectionState.title) { newSection.title = tableRowSectionState.title; }
            if (tableRowSectionState.code) { newSection.code = tableRowSectionState.code; }
            if (Array.isArray(tableRowSectionState.author) && tableRowSectionState.author.length > 0) {
                newSection.author = tableRowSectionState.author;
            }
            if (tableRowSectionState.focus) { newSection.focus = tableRowSectionState.focus; }
            if (tableRowSectionState.text) { newSection.text = tableRowSectionState.text; }
            if (tableRowSectionState.orderedBy) { newSection.orderedBy = tableRowSectionState.orderedBy; }
            if (Array.isArray(tableRowSectionState.entry) && tableRowSectionState.entry.length > 0) {
                newSection.entry = tableRowSectionState.entry;
            }
            if (tableRowSectionState.emptyReason) { newSection.emptyReason = tableRowSectionState.emptyReason; }
            if (Array.isArray(tableRowSectionState.section) && tableRowSectionState.section.length > 0) {
                newSection.section = tableRowSectionState.section;
            }
            newSection = autoEditEmptyReason(newSection);
            if (Object.keys(newSection).length === 0 || (Object.keys(newSection).length === 1 && newSection.code)) {
                newSection = null;
            }
            newSection = JSON.parse(JSON.stringify(newSection));
            if (sectionPathString || sectionPathString === 0 || sectionPathString === "0") {
                let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
                let sectionPath = sectionPathString.split(",");
                if (sectionPath.length === 1) {
                    newResourceDetail.revisedResourceJson.section[sectionPath[0]] = newSection;
                } else if (sectionPath.length === 2) {
                    newResourceDetail.revisedResourceJson.section[sectionPath[0]].section[sectionPath[1]] = newSection;
                } else if (sectionPath.length === 3) {
                    newResourceDetail.revisedResourceJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]] = newSection;
                } else if (sectionPath.length === 4) {
                    newResourceDetail.revisedResourceJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]].section[sectionPath[3]] = newSection;
                }
                console.log("resourceUpdate for Composition.section");
                dispatch(resourceUpdated(newResourceDetail));
                updateChangeReport(dispatch, resourceChanges, newResourceDetail.id,
                    "Composition.section",
                    "section[" + sectionPathString + "].section",
                    startingSection,
                    newSection);
            }
        }
    }), [tableRowSectionState]);

    return <TableRowSectionDataEntry startingValue={startingSection}
        setTableRowSectionState={setTableRowSectionState}
        resourceLoaded={resourceLoaded}
        sampleSizeDefaultState={sampleSizeDefaultState} />
});
/*
            resourceLoaded is a state with shape of ({
                "title": fhirJson.title,
                "sections": fhirJson.section,
                "json": fhirJson,
                "generatedNarrative": generatedNarrative,
                "resourceDictionary": resourceDictionary,
                "resultsSectionsKeys": resultsSectionsKeys,
                "resultsSectionsDictionary": resultsSectionsDictionary,
                "containedDictionary": containedDictionary,
                "combinedDictionary": combinedDictionary,
                "groupReferencesDictionary": groupReferencesDictionary,
                "evidenceDisplayDictionary": evidenceDisplayDictionary,
                "hasResourceCollection": Object.keys(resourceDictionary).length > 0
            });
*/

const EvidenceTableEdit = ({ resourceLoaded, parentResourceDetail }) => {

    const resultsSectionsKeys = resourceLoaded.resultsSectionsKeys;
    const resultsSectionsDictionary = resourceLoaded.resultsSectionsDictionary;
    const groupReferencesDictionary = resourceLoaded.groupReferencesDictionary;
    const groupKeys = Object.keys(groupReferencesDictionary);
    const combinedDictionary = resourceLoaded.combinedDictionary;
    const outcomeGroupsQuantityDictionary = {};
    for (let groupId of groupKeys) {
        if (groupId.split("/").length === 2) {
            groupId = groupId.split("/")[1];
        }
        if (combinedDictionary[groupId]?.quantity || combinedDictionary[groupId]?.quantity === 0) {
            outcomeGroupsQuantityDictionary[groupId] = combinedDictionary[groupId].quantity;
        }
    }

    const [sampleSizeDefaultState, setSampleSizeDefaultState] = useState({});
    const [openAddTableRowModalState, setOpenAddTableRowModalState] = useState({ "openModal": false });

    return <>
        {openAddTableRowModalState.openModal &&
            <AddTableRowModal resourceLoaded={resourceLoaded}
                setOpenAddTableRowModalState={setOpenAddTableRowModalState}
                reportType={resourceLoaded.json?.type?.text} />}
        {groupKeys.length ?
            <>
                <Table>
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell>Variable Name</Table.HeaderCell>
                            {groupKeys.map((groupKey, groupIndex) => {
                                return <Table.HeaderCell key={groupIndex}>
                                    {groupReferencesDictionary[groupKey].display}
                                </Table.HeaderCell>
                            })}
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {(outcomeGroupsQuantityDictionary && Object.keys(outcomeGroupsQuantityDictionary).length > 0) &&
                            <Table.Row key={"topline"}>
                                <Table.Cell style={{ verticalAlign: "top" }}></Table.Cell>
                                {groupKeys.map((groupKey, groupIndex) => {
                                    return <Table.Cell key={groupIndex} style={{ verticalAlign: "top" }}>
                                        {(outcomeGroupsQuantityDictionary[groupKey] || outcomeGroupsQuantityDictionary[groupKey] === 0) && <p>
                                            <span className={"unselectable"} style={{ cursor: "pointer" }}
                                                onClick={() => {
                                                    setSampleSizeDefaultState(prevState => {
                                                        return {
                                                            ...prevState, [groupKey]: outcomeGroupsQuantityDictionary[groupKey],
                                                            [groupIndex]: true
                                                        }
                                                    })
                                                }}>
                                                {(!sampleSizeDefaultState[groupIndex]) &&
                                                    <>➕ Add {outcomeGroupsQuantityDictionary[groupKey]} as Sample Size</>}
                                            </span>
                                        </p>}
                                    </Table.Cell>
                                })}
                            </Table.Row>}
                        {(resultsSectionsKeys.length > 0) &&
                            resultsSectionsKeys.sort().map((sectionKey) => {
                                return <Table.Row key={sectionKey}>
                                    <TableRowEdit startingValue={resultsSectionsDictionary[sectionKey]}
                                        sectionPathString={sectionKey}
                                        foi={parentResourceDetail.id}
                                        sampleSizeDefaultState={sampleSizeDefaultState}
                                        resourceLoaded={resourceLoaded} />
                                </Table.Row>
                            })}
                    </Table.Body>
                </Table>
            </>
            :
            <><p>There are no results sections to produce an evidence table.</p></>
        }
        <Button style={{ color: "#FFFFFF", width: "230px", float: "left" }} className="formButton"
            content="+ Add Table Row" positive
            onClick={() => {
                setOpenAddTableRowModalState({ openModal: true });
            }}
        />
        <br /><br />
    </>
};

export default EvidenceTableEdit;